import * as React from "react";
import * as mapboxgl from "mapbox-gl";
import { css } from "@emotion/core";
import { useAnnotationActions, useAnnotationSubscription } from "context/";

interface IAnnotationMarkerProps {
  map: any;
  annotation: any;
}

const AnnotationMarker = ({ annotation, map }: IAnnotationMarkerProps) => {
  const markerRef = React.useRef(null);

  const { focus, unfocus, select } = useAnnotationActions();
  const { focused, selected } = useAnnotationSubscription(annotation);

  const hidden = annotation.layer.visible === false;

  const styles = {
    marker: css`
      width: 20px;
      height: 20px;
      position: relative;
      color: ${annotation.color || "var(--electra)"};
      fill: ${annotation.color || "var(--electra)"};
      display: block;
      right: 20%;
      opacity: 1;
      transform: scale(1);
      transition:
        all 400ms,
        display 401ms;
      cursor: pointer;
    `,
    markerSelected: css`
      transform: scale(1.5);
    `,
    markerFocused: css`
      filter: drop-shadow(0 0 4px var(--neutral-7));
      transition: 400ms;
    `,
    markerHidden: css`
      opacity: 0;
      /* display: none; */
      pointer-events: none;
      transition:
        display 400ms,
        opacity 399ms;
    `,
    labelVisible: css`
      display: block;
      opacity: 1;
    `,
  };

  React.useEffect(() => {
    if (!map || !annotation) return;
    const marker = new mapboxgl.Marker(markerRef.current)
      .setLngLat(annotation.geometry.coordinates)
      .addTo(map);

    return () => marker.remove();
  }, [map, annotation]);

  return (
    <span>
      {/* ☝
      ️☝️ THIS IS A LOAD-BEARING SPAN! You will get terrifying 'NotFoundErrors' if you don't have it here.
      The reason is still a bit esoteric to me, but it has something to do with the control that Mapbox asserts over
      the element vs. the control that React asserts over the element. If the top-level object (from the component's
      perspective) is the one with the ref, then they fight and nobody wins.
      https://stackoverflow.com/questions/49209223/notfounderror-failed-to-execute-removechild-on-node-the-node-to-be-removed/49722207
      */}
      <span
        ref={markerRef}
        onClick={() => {
          annotation.inspect();
          select(annotation.id);
        }}
        onMouseOver={() => focus(annotation.id)}
        onMouseOut={() => unfocus(annotation.id)}
      >
        <svg
          css={[
            styles.marker,
            selected && styles.markerSelected,
            focused && styles.markerFocused,
            hidden && styles.markerHidden,
          ]}
          version="1.1"
          viewBox="0 0 20 20"
          xmlSpace="preserve"
        >
          <g id="map_marker">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              fill={annotation.color}
              d="M9.98,0c-3.87,0-7,2.98-7,6.67c0,3.68,7,13.33,7,13.33s7-9.65,7-13.33
                 C16.98,2.99,13.84,0,9.98,0z M9.98,10c-1.66,0-3-1.34-3-3s1.34-3,3-3s3,1.34,3,3S11.64,10,9.98,10z"
            />
          </g>
        </svg>
        <span
          css={[
            css`
              display: block;
              position: absolute;
              top: -2rem;
              width: max-content;
              padding: 0.5rem 1rem;
              background: var(--neutral-7);
              color: var(--neutral-1);
              font-size: 1rem;
              opacity: 0;
              pointer-events: none;
              border-radius: 0.5rem;
            `,
            focused && styles.labelVisible,
          ]}
        >
          {annotation.label}
        </span>
      </span>
    </span>
  );
};

export default AnnotationMarker;
